import axiosConfig from '../utils/axiosConfig';

export interface CategoryOption {
  _id: string;
  name: string;
  description?: string;
  status?: string;
}

export interface WarehouseOption {
  _id: string;
  name: string;
  location?: string;
}

export interface ProductVariantOption {
  variantId: string;
  productId: string;
  productName: string;
  attributes: Record<string, string>;
  barcode: string;
  sku: string;
  unit: string;
}

export interface WarehouseQuantityDetail {
  productName: string;
  attributes: Record<string, string>;
  barcode: string;
  sku: string;
  unitName: string;
  quantity: number;
  // أسعار الوحدة (تُستخدم لحساب إجمالي القيمة عند اختيار نوع السعر)
  retailPrice?: number;
  wholesalePrice?: number;
  customPrice1?: number;
  customPrice2?: number;
  lastCost?: number;
  averageCost?: number;
}

// أنواع الأسعار المتاحة لحساب قيمة المخزون
export type PriceType =
  | ''
  | 'retailPrice'
  | 'wholesalePrice'
  | 'customPrice1'
  | 'customPrice2'
  | 'lastCost'
  | 'averageCost';

export interface WarehouseQuantityRow {
  warehouseId: string;
  warehouseName: string;
  totalQuantity: number;
  details: WarehouseQuantityDetail[];
}

export interface QuantitiesResult {
  warehouses: WarehouseQuantityRow[];
  grandTotal: number;
}

export interface CategoriesResponse {
  success: boolean;
  data: CategoryOption[];
  count?: number;
}

export interface ProductsResponse {
  success: boolean;
  data: ProductVariantOption[];
  total: number;
}

export interface WarehousesResponse {
  success: boolean;
  data: WarehouseOption[];
}

export interface QuantitiesResponse {
  success: boolean;
  data: QuantitiesResult;
}

class CategoryQuantitiesReportService {
  private baseURL = '/category-quantities-report';

  // جلب التصنيفات
  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/categories`);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  // جلب المستودعات
  async getWarehouses(): Promise<WarehousesResponse> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/warehouses`);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      throw error;
    }
  }

  // جلب المنتجات/المتغيرات (حسب التصنيف أو نص البحث)
  async getProducts(category: string = '', search: string = '', limit: number = 300): Promise<ProductsResponse> {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      params.append('limit', limit.toString());

      const response = await axiosConfig.get(`${this.baseURL}/products?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  // حساب كميات المستودعات للمنتجات/المتغيرات المختارة
  async getQuantities(
    variantIds: string[],
    category: string = '',
    warehouseIds: string[] = []
  ): Promise<QuantitiesResponse> {
    try {
      const response = await axiosConfig.post(`${this.baseURL}/quantities`, {
        variantIds,
        category,
        warehouseIds
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse quantities:', error);
      throw error;
    }
  }
}

const categoryQuantitiesReportService = new CategoryQuantitiesReportService();
export default categoryQuantitiesReportService;
